import type { ResearchResult } from '../core/intelligence/lead-research.js'
import { NO_DATA_SENTINEL } from '../core/contracts.js'

const GENERIC_LEAD_ROLES = new Set([
  '',
  'unknown',
  'business professional',
  'decision maker',
  'representative',
  'contact',
  'contact / representative',
  'plumbing professional',
  'plumbing professional / representative',
])

const SPECIFIC_ROLE_TITLE =
  /\b(daglig leder|styreleder|styremedlem|founder|co-founder|owner|eier|ceo|cto|cfo|coo|managing director|president|partner|innehaver)\b/i

export function isGenericLeadRole(role?: string | null): boolean {
  const value = (role || '').trim().toLowerCase()
  if (!value || GENERIC_LEAD_ROLES.has(value)) return true
  if (SPECIFIC_ROLE_TITLE.test(value)) return false
  if (/\bprofessional\b/.test(value)) return true
  if (/\brepresentative\b/.test(value)) return true
  return false
}

/** Briefing + chat SSOT: prefer specific person.role over generic research.role. */
export function resolveLeadFacingRole(input: {
  researchRole?: string | null | undefined
  personRole?: string | null | undefined
  verificationRole?: string | null | undefined
}): string | undefined {
  const ordered = [input.personRole, input.verificationRole, input.researchRole]
  for (const candidate of ordered) {
    const trimmed = candidate?.trim()
    if (trimmed && !isGenericLeadRole(trimmed)) return trimmed
  }
  return undefined
}

export function buildStrategicContextFromResearch(
  research: ResearchResult
): Record<string, unknown> | undefined {
  const strategic = research.strategic
  if (!strategic) return undefined

  const competitors = strategic.competitors?.filter(Boolean) ?? []
  const painPoints = strategic.pain_points?.filter(Boolean) ?? []

  return {
    privacySensitivity: 'MEDIUM',
    technicalLevel:
      research.company?.industry?.toLowerCase().includes('tech') ||
      research.person?.role?.toLowerCase().includes('tech')
        ? 'HIGH'
        : 'LOW',
    authorityLevel:
      research.person?.seniority?.match(/C-Level|VP|Director/i) ||
      research.person?.role?.match(
        /CEO|CTO|CFO|COO|Founder|Owner|President|Partner|Managing Director/i
      )
        ? 'DECISION_MAKER'
        : research.person?.seniority?.match(/Manager/i)
          ? 'INFLUENCER'
          : 'RESEARCHER',
    ...(strategic.digital_maturity && strategic.digital_maturity !== NO_DATA_SENTINEL
      ? { digital_maturity: strategic.digital_maturity }
      : {}),
    ...(strategic.financial_signal && strategic.financial_signal !== NO_DATA_SENTINEL
      ? { financial_signal: strategic.financial_signal }
      : {}),
    ...(strategic.internal_gap && strategic.internal_gap !== NO_DATA_SENTINEL
      ? { internal_gap: strategic.internal_gap }
      : {}),
    ...(strategic.competitive_context && strategic.competitive_context !== NO_DATA_SENTINEL
      ? { competitive_context: strategic.competitive_context }
      : {}),
    ...(strategic.personal_hook && strategic.personal_hook !== NO_DATA_SENTINEL
      ? { personal_hook: strategic.personal_hook }
      : {}),
    ...(strategic.pitch_strategy && strategic.pitch_strategy !== NO_DATA_SENTINEL
      ? { pitch_strategy: strategic.pitch_strategy }
      : {}),
    ...(competitors.length > 0 ? { competitors } : {}),
    ...(painPoints.length > 0 ? { pain_points: painPoints } : {}),
  }
}

export interface IdentityVerificationSnapshot {
  verified?: boolean
  confidence?: number
  role?: string
  company?: string
  foundedYear?: string
  productSummary?: string
  careerHighlights?: string[]
  sources?: string[]
  discrepancies?: string[]
}

function preferVerifiedCompanyName(
  researchName?: string,
  verifyName?: string
): string | undefined {
  const research = researchName?.trim()
  const verified = verifyName?.trim()
  if (!verified) return research
  if (!research) return verified
  const looksLikeDomainSlug =
    !research.includes(' ') &&
    research.length <= 24 &&
    /^[a-z0-9.-]+$/i.test(research)
  if (looksLikeDomainSlug) return verified
  if (verified.length > research.length + 2) return verified
  return research
}

/** Prefer explicit payload verification; fall back to client snapshot (TermsOverlay). */
export function mergeIdentityVerification(
  primary?: IdentityVerificationSnapshot,
  fallback?: IdentityVerificationSnapshot
): IdentityVerificationSnapshot | undefined {
  if (!primary && !fallback) return undefined
  const merged = { ...fallback, ...primary }
  const confidence = Math.max(primary?.confidence ?? 0, fallback?.confidence ?? 0)
  if (confidence > 0) merged.confidence = confidence
  return merged
}

export function isHighConfidenceVerification(
  verification?: IdentityVerificationSnapshot
): boolean {
  return Boolean(verification?.verified && (verification.confidence ?? 0) >= 0.7)
}

export function shouldRevealPublicBrief(input: {
  trustedIdentity?: boolean
  research?: Pick<ResearchResult, 'citations'> | { citations?: unknown[] }
  intelligenceContext?: {
    trustedIdentity?: boolean
    research?: { trustedIdentity?: boolean; citations?: unknown[] }
    verification?: IdentityVerificationSnapshot
  }
  verification?: IdentityVerificationSnapshot
}): boolean {
  const verification = mergeIdentityVerification(
    input.verification,
    input.intelligenceContext?.verification
  )
  const citations =
    input.research?.citations ??
    (Array.isArray(input.intelligenceContext?.research?.citations)
      ? input.intelligenceContext?.research?.citations
      : undefined)
  if (Array.isArray(citations) && citations.length > 0) return true

  const hasTrustedIdentity = Boolean(
    input.trustedIdentity === true ||
      input.intelligenceContext?.trustedIdentity === true ||
      input.intelligenceContext?.research?.trustedIdentity === true ||
      verification?.verified === true
  )
  return isHighConfidenceVerification(verification) || hasTrustedIdentity
}

/** Surface verify-identity role/company/sources when init-session research is sparse. */
/** Session-wide research after verify-identity (brief card + chat + greeting). */
export function buildVerifiedSessionResearch(
  research: ResearchResult,
  verification?: IdentityVerificationSnapshot
): ResearchResult {
  if (!isHighConfidenceVerification(verification)) return research
  return enrichResearchForPublicBrief(research, verification)
}

/** Push verified role/company/citations into intelligenceContext for agents + intake answers. */
export function patchIntelligenceContextWithVerifiedResearch(
  base: Record<string, unknown> | undefined,
  research: ResearchResult,
  verification?: IdentityVerificationSnapshot
): Record<string, unknown> {
  const mergedBase = { ...(base || {}) }
  if (verification) mergedBase.verification = verification

  if (!isHighConfidenceVerification(verification)) return mergedBase

  const enriched = enrichResearchForPublicBrief(research, verification)
  const role = enriched.role || enriched.person?.role
  const companyName = enriched.company?.name || verification?.company
  const baseCompany =
    mergedBase.company && typeof mergedBase.company === 'object'
      ? (mergedBase.company as Record<string, unknown>)
      : {}
  const basePerson =
    mergedBase.person && typeof mergedBase.person === 'object'
      ? (mergedBase.person as Record<string, unknown>)
      : {}
  const baseResearch =
    mergedBase.research && typeof mergedBase.research === 'object'
      ? (mergedBase.research as Record<string, unknown>)
      : {}

  return {
    ...mergedBase,
    identityConfirmed: true,
    trustedIdentity: true,
    researchConfidence: enriched.confidence,
    ...(companyName
      ? {
          company: {
            ...baseCompany,
            name: companyName,
            ...(enriched.company?.industry ? { industry: enriched.company.industry } : {}),
            ...(enriched.company?.domain ? { domain: enriched.company.domain } : {}),
            ...(enriched.company?.website ? { website: enriched.company.website } : {}),
            ...(enriched.company?.summary ? { summary: enriched.company.summary } : {}),
          },
        }
      : {}),
    ...(enriched.person?.fullName || role
      ? {
          person: {
            ...basePerson,
            ...(enriched.person?.fullName ? { fullName: enriched.person.fullName } : {}),
            ...(role ? { role } : {}),
            ...(enriched.person?.company ? { company: enriched.person.company } : {}),
          },
        }
      : {}),
    ...(role ? { role } : {}),
    research: {
      ...baseResearch,
      citations: enriched.citations || [],
      trustedIdentity: true,
    },
    ...(buildStrategicContextFromResearch(enriched)
      ? { strategicContext: buildStrategicContextFromResearch(enriched) }
      : {}),
  }
}

/** Merge source-backed research into intelligenceContext for briefing + chat parity. */
export function mergeResearchIntoIntelligenceContext(
  base: Record<string, unknown> | undefined,
  research: ResearchResult,
  verification?: IdentityVerificationSnapshot
): Record<string, unknown> {
  const merged = isHighConfidenceVerification(verification)
    ? patchIntelligenceContextWithVerifiedResearch(base, research, verification)
    : { ...(base || {}), ...(verification ? { verification } : {}) }

  const sourceBacked = (research.citations?.length ?? 0) > 0
  if (!sourceBacked && !isHighConfidenceVerification(verification)) {
    return merged
  }

  const verificationSnapshot = mergeIdentityVerification(
    verification,
    merged.verification as IdentityVerificationSnapshot | undefined
  )
  const role = resolveLeadFacingRole({
    researchRole: research.role,
    personRole: research.person?.role,
    verificationRole: verificationSnapshot?.role,
  })

  const baseCompany =
    merged.company && typeof merged.company === 'object'
      ? (merged.company as Record<string, unknown>)
      : {}
  const basePerson =
    merged.person && typeof merged.person === 'object'
      ? (merged.person as Record<string, unknown>)
      : {}
  const baseResearch =
    merged.research && typeof merged.research === 'object'
      ? (merged.research as Record<string, unknown>)
      : {}

  const strategicContext = buildStrategicContextFromResearch(research)

  return {
    ...merged,
    ...(sourceBacked && merged.trustedIdentity !== true ? { trustedIdentity: true } : {}),
    researchConfidence: Math.max(
      Number(merged.researchConfidence ?? 0),
      research.confidence ?? 0,
      verificationSnapshot?.confidence ?? 0
    ),
    ...(role ? { role } : {}),
    ...(role || research.person?.fullName
      ? {
          person: {
            ...basePerson,
            ...(research.person?.fullName ? { fullName: research.person.fullName } : {}),
            ...(role ? { role } : {}),
            ...(research.person?.company ? { company: research.person.company } : {}),
          },
        }
      : {}),
    ...(research.company?.name ||
    research.company?.industry ||
    research.company?.summary
      ? {
          company: {
            ...baseCompany,
            ...(research.company?.name ? { name: research.company.name } : {}),
            ...(research.company?.industry ? { industry: research.company.industry } : {}),
            ...(research.company?.summary ? { summary: research.company.summary } : {}),
            ...(research.company?.domain ? { domain: research.company.domain } : {}),
          },
        }
      : {}),
    research: {
      ...baseResearch,
      citations: research.citations ?? [],
      ...(sourceBacked ? { trustedIdentity: true } : {}),
    },
    ...(strategicContext ? { strategicContext } : {}),
  }
}

export function enrichResearchForPublicBrief(
  research: ResearchResult,
  verification?: IdentityVerificationSnapshot
): ResearchResult {
  if (!isHighConfidenceVerification(verification)) return research

  const verificationSources = (verification?.sources ?? []).filter(Boolean)
  const citations =
    research.citations && research.citations.length > 0
      ? research.citations
      : verificationSources.map(uri => ({ uri, title: uri }))

  const role = resolveLeadFacingRole({
    researchRole: research.role,
    personRole: research.person?.role,
    verificationRole: verification?.role,
  })

  const companyName = preferVerifiedCompanyName(
    research.company?.name,
    verification?.company
  )
  const companySummaryParts = [
    verification?.productSummary?.trim(),
    verification?.foundedYear?.trim()
      ? `Founded ${verification.foundedYear.trim()}.`
      : undefined,
    research.company?.summary?.trim(),
  ].filter(Boolean)
  const companySummary = companySummaryParts.length > 0 ? companySummaryParts.join(' ') : undefined
  const personCompany =
    verification?.company &&
    (!research.person?.company ||
      research.person.company.toLowerCase() === research.company?.domain?.toLowerCase() ||
      research.person.company.toLowerCase() === (research.company?.name || '').toLowerCase())
      ? verification.company
      : research.person?.company

  return {
    ...research,
    ...(role ? { role } : {}),
    confidence: Math.max(research.confidence ?? 0, verification?.confidence ?? 0),
    citations,
    company: {
      ...research.company,
      ...(companyName ? { name: companyName } : {}),
      ...(companySummary ? { summary: companySummary } : {}),
    },
    person: {
      ...research.person,
      fullName: research.person?.fullName || '',
      ...(role ? { role } : {}),
      ...(personCompany ? { company: personCompany } : {}),
      ...(verification?.careerHighlights?.length
        ? { careerHighlights: verification.careerHighlights }
        : {}),
    },
  }
}
